import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  Euro, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  AlertTriangle, 
  Info,
  HelpCircle,
  Download,
  Bell,
  X,
  UserX,
  ShieldAlert,
  RefreshCw
} from 'lucide-react';
import API_URL from '../config';
import ExportModal from '../components/ExportModal';

const API_BASE = API_URL;

export default function DashboardAnalyse() {
  const { isAuthenticated, isManager } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [salesData, setSalesData] = useState({}); // Map date -> sale_id
  const [weekStart, setWeekStart] = useState(getStartOfWeek(new Date()));
  const [salesInputs, setSalesInputs] = useState({}); // Local state for inputs
  const [savingSale, setSavingSale] = useState(null); // Date string of sale being saved
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Notifications state
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifications, setNotifications] = useState(null); // null = not fetched yet
  const notifRef = useRef(null);

  // Redirect if not manager
  useEffect(() => {
    if (isAuthenticated && !isManager) {
      navigate('/gestionShifts');
    }
  }, [isAuthenticated, isManager, navigate]);

  // Helper to get Monday of the current week
  function getStartOfWeek(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  function getEndOfWeek(start) {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  }

  function formatDateForAPI(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateDisplay(dateStr) {
    // Parse YYYY-MM-DD in local timezone
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit' }).format(date).toUpperCase();
  }

  const navigateWeek = (direction) => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() + (direction * 7));
    setWeekStart(newStart);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const startStr = formatDateForAPI(weekStart);
      const endStr = formatDateForAPI(getEndOfWeek(weekStart));

      // Fetch Dashboard Metrics
      const dashRes = await fetch(`${API_BASE}/api/dashboard/financial?start_date=${startStr}&end_date=${endStr}&validated_only=true&published_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!dashRes.ok) throw new Error("Erreur de chargement du tableau de bord");
      const dashData = await dashRes.json();
      setDashboardData(dashData);

      // Initialize inputs from fetched data
      const inputs = {};
      const salesMap = {};
      
      // We also need to fetch actual sales objects to get their IDs for updates
      // The dashboard endpoint gives us amounts, but not necessarily IDs if we want to PUT
      // So let's fetch sales for the period to map Date -> ID
      const salesRes = await fetch(`${API_BASE}/api/sales/?start_date=${startStr}&end_date=${endStr}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (salesRes.ok) {
        const salesList = await salesRes.json();
        salesList.forEach(s => {
          salesMap[s.date] = s.id;
          inputs[s.date] = s.amount;
        });
      }

      setSalesData(salesMap);
      setSalesInputs(prev => ({...prev, ...inputs})); // Merge to keep user edits if any (though we reloaded)

    } catch (error) {
      console.error("Dashboard error:", error);
      alert("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isManager) {
      fetchDashboardData();
      fetchNotifications(weekStart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, isManager]);

  // Close notification panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = useCallback(async (targetWeekStart) => {
    try {
      setNotifLoading(true);
      const token = localStorage.getItem('token');

      // 1. Fetch all users
      const usersRes = await fetch(`${API_BASE}/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const users = usersRes.ok ? await usersRes.json() : [];

      const missingWeeklyHours = users.filter(
        u => u.is_active && (u.weekly_hours === null || u.weekly_hours === undefined)
      );
      const missingMonthlySalary = users.filter(
        u => u.is_active && (u.monthly_gross_salary === null || u.monthly_gross_salary === undefined)
      );

      // 2. Fetch shifts for the DISPLAYED week
      const refWeekStart = targetWeekStart || getStartOfWeek(new Date());
      const startStr = formatDateForAPI(refWeekStart);
      const endStr = formatDateForAPI(getEndOfWeek(refWeekStart));
      const todayStr = formatDateForAPI(new Date());

      const shiftsRes = await fetch(`${API_BASE}/shifts/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let unvalidatedPastShifts = [];
      let unvalidatedFutureShifts = [];
      if (shiftsRes.ok) {
        const allShifts = await shiftsRes.json();
        const weekShifts = allShifts.filter(s => {
          if (!s.start_time) return false;
          const shiftDate = s.start_time.split('T')[0];
          return shiftDate >= startStr && shiftDate <= endStr && s.is_published && !s.validated && !s.no_show;
        });
        // Split: past (today included) vs future
        unvalidatedPastShifts = weekShifts.filter(s => s.start_time.split('T')[0] <= todayStr);
        unvalidatedFutureShifts = weekShifts.filter(s => s.start_time.split('T')[0] > todayStr);
      }

      setNotifications({ missingWeeklyHours, missingMonthlySalary, unvalidatedPastShifts, unvalidatedFutureShifts, weekLabel: `${refWeekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${getEndOfWeek(refWeekStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` });
    } catch (err) {
      console.error('Notifications fetch error:', err);
      setNotifications({ missingWeeklyHours: [], missingMonthlySalary: [], unvalidatedPastShifts: [], unvalidatedFutureShifts: [], weekLabel: '' });
    } finally {
      setNotifLoading(false);
    }
  }, []);

  const handleNotifToggle = () => {
    if (!notifOpen) {
      fetchNotifications(weekStart);
    }
    setNotifOpen(prev => !prev);
  };

  const totalAlerts = notifications
    ? notifications.missingWeeklyHours.length + notifications.missingMonthlySalary.length + notifications.unvalidatedPastShifts.length
    : null;

  const handleSaleChange = (date, value) => {
    setSalesInputs(prev => ({
      ...prev,
      [date]: value
    }));
  };

  const saveSale = async (date) => {
    const amount = parseFloat(salesInputs[date]);
    if (isNaN(amount) || amount < 0) return;

    try {
      setSavingSale(date);
      const token = localStorage.getItem('token');
      const saleId = salesData[date];

      if (saleId) {
        // Update existing
        const response = await fetch(`${API_BASE}/api/sales/${saleId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ amount })
        });
        if (!response.ok) throw new Error("Failed to update sale");
      } else {
        // Create new
        const response = await fetch(`${API_BASE}/api/sales/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ date, amount })
        });
        if (!response.ok) throw new Error("Failed to create sale");
      }

      // Refresh data to update calculations
      await fetchDashboardData();

    } catch (error) {
      console.error("Save sale error:", error);
      alert("Erreur lors de la sauvegarde de la vente");
    } finally {
      setSavingSale(null);
    }
  };

  const handleKeyDown = (e, date) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Triggers onBlur
    }
  };

  const handleExport = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/export/shifts-excel?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_shifts_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  };

  // Prepare weekly columns
  const days = [];
  const current = new Date(weekStart);
  for (let i = 0; i < 7; i++) {
    const dateStr = formatDateForAPI(current);
    // Find daily metric
    const metric = dashboardData?.daily_metrics?.find(m => m.date === dateStr) || {};
    
    // Safety check for values
    const hours = Number(metric.hours_worked) || 0;
    const cost = Number(metric.estimated_cost) || 0;
    const sales = salesInputs[dateStr] ? parseFloat(salesInputs[dateStr]) : 0;
    
    // Calculate productivity and ratio with ACTUAL sales (not backend estimates)
    const productivity = (sales > 0 && hours > 0) ? sales / hours : 0;
    const ratio = sales > 0 ? (cost / sales) * 100 : 0;

    days.push({
      date: dateStr,
      label: formatDateDisplay(dateStr),
      salesInput: salesInputs[dateStr] || '',
      hours: hours,
      productivity: productivity,
      cost: cost,
      ratio: ratio
    });
    current.setDate(current.getDate() + 1);
  }

  // KPI Card Component
  const KpiCard = ({ label, value, subValue, icon: Icon, color, subColor, tooltip }) => {
    const colorClasses = {
      blue: "bg-blue-50 text-blue-700 border-blue-100",
      indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
      purple: "bg-purple-50 text-purple-700 border-purple-100",
      emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
      orange: "bg-orange-50 text-orange-700 border-orange-100",
      red: "bg-red-50 text-red-700 border-red-100",
      gray: "bg-gray-50 text-gray-700 border-gray-200",
    };

    const baseClass = colorClasses[color] || colorClasses.gray;

    return (
      <Tooltip text={tooltip}>
        <div className={`rounded-xl p-3 sm:p-4 border transition-all hover:shadow-md ${baseClass} h-full w-full cursor-help`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 opacity-80" />
            <span className="text-xs sm:text-sm font-medium opacity-80 whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
          </div>
          <div className="text-lg sm:text-2xl font-bold">{value}</div>
          {subValue && (
            <div className={`text-xs mt-1 font-medium ${subColor || 'opacity-75'}`}>
              {subValue}
            </div>
          )}
        </div>
      </Tooltip>
    );
  };

  // Tooltip Component
  const Tooltip = ({ text, children }) => {
    if (!text) return children;
    
    return (
      <div className="group relative flex items-center h-full w-full">
        {children}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-max max-w-[200px] px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-50 whitespace-normal text-center pointer-events-none transition-opacity duration-200 opacity-0 group-hover:opacity-100">
          {text}
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
        </div>
      </div>
    );
  };

  if (!isManager) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header & Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analyse Financière</h1>
            <p className="text-gray-500">Rentabilité et Staffing</p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                id="btn-notifications"
                onClick={handleNotifToggle}
                className="relative flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                title="Alertes"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {/* Badge */}
                {totalAlerts !== null && totalAlerts > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm">
                    {totalAlerts > 9 ? '9+' : totalAlerts}
                  </span>
                )}
                {/* Green dot when no alerts */}
                {totalAlerts === 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
                )}
              </button>

              {/* Mobile overlay backdrop */}
              {notifOpen && (
                <div
                  className="fixed inset-0 bg-black/30 z-40 sm:hidden"
                  onClick={() => setNotifOpen(false)}
                />
              )}

              {/* Dropdown / Bottom-sheet Panel */}
              {notifOpen && (
                <div className="
                  fixed bottom-0 left-0 right-0 z-50
                  sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-12 sm:w-96
                  w-full bg-white
                  rounded-t-2xl sm:rounded-xl
                  shadow-2xl border border-gray-200
                  overflow-hidden
                ">
                  {/* Mobile drag handle */}
                  <div className="flex justify-center pt-3 pb-1 sm:hidden">
                    <div className="w-10 h-1 rounded-full bg-gray-300" />
                  </div>

                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-gray-600" />
                      <span className="font-semibold text-gray-800 text-sm">Alertes RH</span>
                      {totalAlerts !== null && totalAlerts > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">{totalAlerts}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fetchNotifications(weekStart)}
                        disabled={notifLoading}
                        className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500 hover:text-gray-700"
                        title="Rafraîchir"
                      >
                        <RefreshCw className={`w-4 h-4 ${notifLoading ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => setNotifOpen(false)}
                        className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="max-h-[60vh] sm:max-h-[420px] overflow-y-auto">
                    {notifLoading && !notifications ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                      </div>
                    ) : notifications && totalAlerts === 0 ? (
                      <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
                        <span className="text-4xl">✅</span>
                        <p className="text-sm font-medium">Aucune alerte en cours</p>
                        <p className="text-xs text-gray-400">Tout est en ordre !</p>
                      </div>
                    ) : notifications ? (
                      <div className="divide-y divide-gray-100">

                        {/* Missing weekly_hours */}
                        {notifications.missingWeeklyHours.length > 0 && (
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-2">
                              <UserX className="w-4 h-4 text-orange-500 flex-shrink-0" />
                              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Heures/semaine manquantes</span>
                              <span className="ml-auto px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full">{notifications.missingWeeklyHours.length}</span>
                            </div>
                            <ul className="space-y-1">
                              {notifications.missingWeeklyHours.map(u => (
                                <li key={u.id} className="flex items-center gap-2 text-sm text-gray-700 py-2 px-2 rounded-lg hover:bg-orange-50 active:bg-orange-100 transition-colors">
                                  <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                                  <span className="font-medium truncate">{u.full_name || u.email}</span>
                                  <span className="ml-auto text-xs text-orange-500 font-medium whitespace-nowrap">Pas d&apos;heures contrat</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Missing monthly_gross_salary */}
                        {notifications.missingMonthlySalary.length > 0 && (
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-2">
                              <ShieldAlert className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Salaire mensuel manquant</span>
                              <span className="ml-auto px-1.5 py-0.5 bg-amber-100 text-amber-600 text-xs font-bold rounded-full">{notifications.missingMonthlySalary.length}</span>
                            </div>
                            <ul className="space-y-1">
                              {notifications.missingMonthlySalary.map(u => (
                                <li key={u.id} className="flex items-center gap-2 text-sm text-gray-700 py-2 px-2 rounded-lg hover:bg-amber-50 active:bg-amber-100 transition-colors">
                                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                                  <span className="font-medium truncate">{u.full_name || u.email}</span>
                                  <span className="ml-auto text-xs text-amber-500 font-medium whitespace-nowrap">Pas de salaire mensuel</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Unvalidated PAST shifts — real alert */}
                        {notifications.unvalidatedPastShifts.length > 0 && (
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Non validés — passés {notifications.weekLabel ? `(${notifications.weekLabel})` : ''}</span>
                              <span className="ml-auto px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">{notifications.unvalidatedPastShifts.length}</span>
                            </div>
                            <ul className="space-y-1">
                              {notifications.unvalidatedPastShifts.map(s => {
                                const shiftDay = s.start_time ? new Date(s.start_time).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—';
                                return (
                                  <li key={s.id} className="flex items-center gap-2 text-sm text-gray-700 py-2 px-2 rounded-lg hover:bg-red-50 active:bg-red-100 transition-colors">
                                    <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                                    <span className="font-medium truncate">{s.user_name || `User #${s.user_id}`}</span>
                                    <span className="ml-auto text-xs text-red-500 font-medium whitespace-nowrap">{shiftDay}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* Unvalidated FUTURE shifts — informational only */}
                        {notifications.unvalidatedFutureShifts.length > 0 && (
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pas encore réalisés {notifications.weekLabel ? `(${notifications.weekLabel})` : ''}</span>
                              <span className="ml-auto px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs font-bold rounded-full">{notifications.unvalidatedFutureShifts.length}</span>
                            </div>
                            <ul className="space-y-1">
                              {notifications.unvalidatedFutureShifts.map(s => {
                                const shiftDay = s.start_time ? new Date(s.start_time).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—';
                                return (
                                  <li key={s.id} className="flex items-center gap-2 text-sm text-gray-500 py-2 px-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors">
                                    <span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                                    <span className="truncate">{s.user_name || `User #${s.user_id}`}</span>
                                    <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">{shiftDay}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Footer */}
                  {notifications && totalAlerts > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                      <p className="text-xs text-gray-400 text-center">
                        Corrigez ces points pour avoir une analyse complète
                      </p>
                    </div>
                  )}

                  {/* Mobile bottom safe area */}
                  <div className="h-safe-bottom sm:hidden pb-4" />
                </div>
              )}
            </div>

            {/* Export Button */}
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
            
            {/* Week Navigation */}
            <div className="flex items-center gap-2 sm:gap-4 bg-white p-1.5 sm:p-2 rounded-lg shadow-sm border border-gray-200">
              <button onClick={() => navigateWeek(-1)} className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
              <span className="font-medium text-gray-900 text-xs sm:text-sm min-w-[140px] sm:min-w-[200px] text-center select-none">
                {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {getEndOfWeek(weekStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' })}
              </span>
              <button onClick={() => navigateWeek(1)} className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {loading && !dashboardData ? (
           <div className="flex justify-center py-20">
             <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
           </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
              <KpiCard 
                label="Ventes (HT)" 
                tooltip="Total des ventes hors taxes saisies pour la semaine"
                value={`${Number(dashboardData?.total_sales || 0).toFixed(2)} €`}
                icon={Euro}
                color="blue"
              />
              <KpiCard 
                label="Coût Shifts" 
                tooltip="Coût estimé de la main d'œuvre (Heures × Taux + Heures Supp.)"
                value={`${Number(dashboardData?.total_estimated_cost || 0).toFixed(2)} €`} 
                subValue={dashboardData?.total_overtime_cost > 0 ? `Dont ${Number(dashboardData.total_overtime_cost).toFixed(2)}€ HS` : null}
                icon={TrendingUp}
                color="indigo"
              />
              <KpiCard 
                label="Heures" 
                tooltip="Total des heures travaillées sur la semaine (shifts validés)"
                value={`${dashboardData?.total_hours || 0}h`} 
                icon={Clock}
                color="purple"
              />
              <KpiCard 
                label="Productivité" 
                tooltip="Chiffre d'affaires moyen généré par heure travaillée"
                value={`${Number(dashboardData?.overall_productivity || 0).toFixed(2)} €/h`} 
                icon={BarChart3}
                color="emerald"
              />
               <KpiCard 
                label="Coût/Ventes" 
                tooltip="Ratio de la masse salariale sur le chiffre d'affaires"
                value={`${Number(dashboardData?.cost_to_sales_ratio || 0).toFixed(1)}%`} 
                icon={Info}
                color={dashboardData?.cost_to_sales_ratio > 35 ? "red" : "emerald"}
              />
              <KpiCard 
                label="Heures Supp." 
                tooltip="Total des heures supplémentaires (au-delà du contrat hebdo)"
                value={`${dashboardData?.total_overtime_hours || 0}h`}
                subValue={dashboardData?.total_overtime_cost > 0 ? `${Number(dashboardData.total_overtime_cost).toFixed(2)} €` : null}
                icon={AlertTriangle}
                color={dashboardData?.total_overtime_hours > 0 ? "orange" : "gray"}
                subColor="text-orange-700"
              />
            </div>

            {/* Main Grid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 relative">
              {/* Mobile Scroll Hint */}
              <div className="sm:hidden absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-20"></div>
              
              <div className="overflow-x-auto pb-2">
                <table className="w-full min-w-[700px] sm:min-w-[800px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 sm:w-48 sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Indicateurs</th>
                      {days.map(day => (
                        <th key={day.date} className="px-4 py-4 text-center text-sm font-semibold text-gray-700 w-32 bg-gray-50">
                          <div className={`inline-block px-3 py-1 rounded-full ${day.date === formatDateForAPI(new Date()) ? 'bg-indigo-100 text-indigo-700' : ''}`}>
                            {day.label}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {/* Row: Real Sales (Input) */}
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <Tooltip text="Saisissez ici le chiffre d'affaires journalier hors taxes (HT)">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 cursor-help">
                            <span>Ventes (HT)</span>
                            <HelpCircle className="w-3 h-3 text-gray-400 hidden sm:block" />
                          </div>
                        </Tooltip>
                      </td>
                      {days.map(day => (
                        <td key={day.date} className="px-4 py-3">
                           <div className="relative group">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={day.salesInput}
                              onChange={(e) => handleSaleChange(day.date, e.target.value)}
                              onBlur={() => saveSale(day.date)}
                              onKeyDown={(e) => handleKeyDown(e, day.date)}
                              disabled={savingSale === day.date}
                              className={`w-full text-center px-2 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all font-semibold text-gray-900 ${
                                savingSale === day.date ? 'bg-gray-50 text-gray-400' : 'bg-white border-gray-200 hover:border-indigo-300'
                              }`}
                              placeholder="0.00"
                            />
                            {savingSale === day.date && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                              </div>
                            )}
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">€</div>
                           </div>
                        </td>
                      ))}
                    </tr>

                    {/* Row: Hours Worked */}
                    <tr>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <Tooltip text="Cumul des heures de tous les employés pour la journée">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 cursor-help">
                            <span>Heures</span>
                            <HelpCircle className="w-3 h-3 text-gray-400 hidden sm:block" />
                          </div>
                        </Tooltip>
                      </td>
                      {days.map(day => (
                        <td key={day.date} className="px-4 py-4 text-center text-sm text-gray-600">
                          {day.hours > 0 ? (
                            <span className="font-medium bg-gray-100 px-2.5 py-1 rounded-md text-gray-700">
                              {day.hours}h
                            </span>
                          ) : '-'}
                        </td>
                      ))}
                    </tr>

                    {/* Row: Productivity */}
                    <tr className="bg-gray-50/30">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <Tooltip text="Chiffre d'affaires journalier divisé par les heures travaillées">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 cursor-help">
                            <span>Productivité</span>
                            <HelpCircle className="w-3 h-3 text-gray-400 hidden sm:block" />
                          </div>
                        </Tooltip>
                      </td>
                      {days.map(day => (
                         <td key={day.date} className="px-4 py-4 text-center text-sm bg-gray-50/30">
                           {day.productivity > 0 ? (
                             <span className={`font-semibold ${day.productivity < 40 ? 'text-orange-600' : 'text-emerald-600'}`}>
                               {day.productivity.toFixed(1)} €/h
                             </span>
                           ) : '-'}
                         </td>
                      ))}
                    </tr>

                    {/* Row: Estimated Cost */}
                    <tr>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                         <Tooltip text="Coût salarial journalier estimé (Heures × Taux horaire)">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 cursor-help">
                            <span>Coût estimé</span>
                            <HelpCircle className="w-3 h-3 text-gray-400 hidden sm:block" />
                          </div>
                        </Tooltip>
                      </td>
                      {days.map(day => (
                        <td key={day.date} className="px-4 py-4 text-center text-sm">
                          {day.cost > 0 ? (
                            <span className="text-gray-900 font-medium">
                              {day.cost.toFixed(0)} €
                            </span>
                          ) : '-'}
                        </td>
                      ))}
                    </tr>

                    {/* Row: Cost Ratio */}
                    <tr>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <Tooltip text="Ratio coût salarial / chiffre d'affaires du jour">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 cursor-help">
                            <span>Coût / Ventes</span>
                            <HelpCircle className="w-3 h-3 text-gray-400 hidden sm:block" />
                          </div>
                        </Tooltip>
                      </td>
                      {days.map(day => {
                        return (
                          <td key={day.date} className="px-4 py-4 text-center text-sm">
                            {day.ratio > 0 ? (
                              <div className="flex flex-col items-center">
                                <span className={`font-bold ${day.ratio > 35 ? 'text-red-500' : 'text-emerald-600'}`}>
                                  {day.ratio.toFixed(1)}%
                                </span>
                              </div>
                            ) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
          initialStartDate={formatDateForAPI(weekStart)}
          initialEndDate={formatDateForAPI(getEndOfWeek(weekStart))}
        />
      </div>
    </div>
  );
}
